import {
	Banknote,
	Car,
	Clock,
	Coffee,
	Home,
	Infinity as InfinityIcon,
	type LucideIcon,
	PiggyBank,
	Plane,
	Shield,
	Sunset,
	TrendingUp,
	Wallet,
} from "lucide-react";
import retirementImg from "shared/assets/inspiration/ins-020-retirement-dream.jpg";
import type { ObjPathProxy } from "ts-object-path";
import { RentDrawdownMode } from "web_myplann/Goals/Types/RentDrawdownMode";
import {
	ClientPortfolioDomainEnum,
	GoalCategoryTypeEnum,
	ProductCategoryEnum,
} from "web_myplann/Infrastructure/Api/Api";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { InspirationTimelineStep } from "web_myplann/Inspiration/Components/InspirationTimeline";
import {
	INSPIRATION_DEFAULT_SCENARIO,
	INSPIRATION_SCENARIO_KEYS,
	INSPIRATION_SCENARIO_RATES,
} from "web_myplann/Inspiration/constants";
import type {
	RetirementAnchorKey,
	RetirementLoanKind,
	RetirementScenarioKey,
} from "web_myplann/Inspiration/Retirement/Types/RetirementTypes";
import { formatInspirationMoney } from "web_myplann/Inspiration/Utils/inspirationMoney";

export const RETIREMENT_IMAGE = retirementImg;
export const RETIREMENT_GOAL_CATEGORY = GoalCategoryTypeEnum.Pension;

export const RETIREMENT_SAVINGS_DROP_ID = "savings";

export const RETIREMENT_LOAN_DOMAINS = new Set<ClientPortfolioDomainEnum>([
	ClientPortfolioDomainEnum.Loans,
	ClientPortfolioDomainEnum.BusinessLoans,
	ClientPortfolioDomainEnum.Leasing,
]);

export const RETIREMENT_SAVING_DOMAINS = new Set<ClientPortfolioDomainEnum>([
	ClientPortfolioDomainEnum.Investments,
	ClientPortfolioDomainEnum.PensionProducts,
]);

export const RETIREMENT_AGE = 65;
export const RETIREMENT_AGE_MIN = 45;
export const RETIREMENT_AGE_MAX = 70;
export const RETIREMENT_STATE_PENSION_AGE = 65;
export const RETIREMENT_FALLBACK_AGE = 40;
export const RETIREMENT_DRAWDOWN_YEARS = 20;
export const RETIREMENT_READY_COVERAGE = 90;
export const RETIREMENT_MAX_SHOWN_COVERAGE = 999;
export const RETIREMENT_PENSION_FULL_BONUS_FROM = 1_700;
export const RETIREMENT_PENSION_FULL_BONUS_YEAR = 2026;

export const RETIREMENT_INCOME_MIN = 15_000;
export const RETIREMENT_INCOME_STEP = 500;
export const RETIREMENT_ANCHOR_KEYS: RetirementAnchorKey[] = [
	"low",
	"mid",
	"high",
];
export const RETIREMENT_SKELETON_EXPENSE_ROWS = [0, 1, 2];
export const RETIREMENT_ANCHOR_RATIOS: Record<RetirementAnchorKey, number> = {
	low: 0.85,
	mid: 1,
	high: 1.25,
};
export const RETIREMENT_FIXED_ANCHORS: Record<RetirementAnchorKey, number> = {
	low: 25_000,
	mid: 40_000,
	high: 60_000,
};

export const RETIREMENT_STATE_PENSION_DEFAULT = 22_000;
export const RETIREMENT_STATE_PENSION_MIN = 10_000;
export const RETIREMENT_STATE_PENSION_MAX = 40_000;
export const RETIREMENT_STATE_PENSION_STEP = 500;

export const RETIREMENT_CONTRIBUTION_MIN = 0;
export const RETIREMENT_CONTRIBUTION_STEP = 500;

export const RETIREMENT_EXPENSES_MIN = 5_000;
export const RETIREMENT_EXPENSES_MAX = 200_000;
export const RETIREMENT_EXPENSES_STEP = 1_000;
export const RETIREMENT_EXPENSES_DEFAULT = 30_000;

export const RETIREMENT_LOAN_AMOUNT_MIN = 500;
export const RETIREMENT_LOAN_AMOUNT_MAX = 50_000;
export const RETIREMENT_LOAN_AMOUNT_STEP = 500;
export const RETIREMENT_LOAN_AMOUNT_DEFAULT = 5_000;
export const RETIREMENT_LOAN_YEARS_SPAN = 40;
export const RETIREMENT_LOAN_DEFAULT_YEARS_AHEAD = 10;

export const RETIREMENT_EXAMPLE = {
	expenses: 40_000,
	mortgage: 12_000,
	mortgageEndYear: 2041,
	savings: 3_000,
	statePension: 18_000,
};

const RetirementResources = Resources.Inspiration.Retirement;

export type RetirementScenarioOption = {
	key: RetirementScenarioKey;
	rate: number;
	recommended: boolean;
};

export const RETIREMENT_SCENARIOS: RetirementScenarioOption[] =
	INSPIRATION_SCENARIO_KEYS.map((key) => ({
		key,
		rate: INSPIRATION_SCENARIO_RATES[key],
		recommended: key === INSPIRATION_DEFAULT_SCENARIO,
	}));

export const RETIREMENT_ANCHOR_ICONS: Record<RetirementAnchorKey, LucideIcon> =
	{
		low: Coffee,
		mid: Home,
		high: Plane,
	};

export const RETIREMENT_ANCHOR_COPY: Record<
	RetirementAnchorKey,
	ObjPathProxy<MyPlannResourceDictionary, string>
> = {
	low: RetirementResources.Anchors.low,
	mid: RetirementResources.Anchors.mid,
	high: RetirementResources.Anchors.high,
};

export enum RetirementDropSource {
	Portfolio = "Portfolio",
	Savings = "Savings",
	Manual = "Manual",
}

export enum RetirementDropNoteType {
	EndsBefore = "EndsBefore",
	EndsAfter = "EndsAfter",
	UnknownEnd = "UnknownEnd",
	Savings = "Savings",
}

export const RETIREMENT_DROP_NOTE_COPY: Record<
	RetirementDropNoteType,
	ObjPathProxy<MyPlannResourceDictionary, string>
> = {
	[RetirementDropNoteType.EndsBefore]: RetirementResources.DropNotes.endsBefore,
	[RetirementDropNoteType.EndsAfter]: RetirementResources.DropNotes.endsAfter,
	[RetirementDropNoteType.UnknownEnd]: RetirementResources.DropNotes.unknownEnd,
	[RetirementDropNoteType.Savings]: RetirementResources.DropNotes.savings,
};

export const RETIREMENT_LOAN_KIND_VALUES = [
	ProductCategoryEnum.HousingLoans,
	ProductCategoryEnum.ConsumerLoans,
	ProductCategoryEnum.Leasing,
	ProductCategoryEnum.OtherLoans,
] as const satisfies readonly RetirementLoanKind[];

export const RETIREMENT_LOAN_KIND_ICONS: Record<
	RetirementLoanKind,
	LucideIcon
> = {
	[ProductCategoryEnum.HousingLoans]: Home,
	[ProductCategoryEnum.ConsumerLoans]: Banknote,
	[ProductCategoryEnum.Leasing]: Car,
	[ProductCategoryEnum.OtherLoans]: Wallet,
};

export const RETIREMENT_LOAN_KIND_COPY: Record<
	RetirementLoanKind,
	ObjPathProxy<MyPlannResourceDictionary, string>
> = {
	[ProductCategoryEnum.HousingLoans]:
		RetirementResources.LoanKinds.HousingLoans,
	[ProductCategoryEnum.ConsumerLoans]:
		RetirementResources.LoanKinds.ConsumerLoans,
	[ProductCategoryEnum.Leasing]: RetirementResources.LoanKinds.Leasing,
	[ProductCategoryEnum.OtherLoans]: RetirementResources.LoanKinds.OtherLoans,
};

export type RetirementDrawdownOption = {
	mode: RentDrawdownMode;
	icon: LucideIcon;
	copy: ObjPathProxy<
		MyPlannResourceDictionary,
		{ label: string; description: string }
	>;
};

export const RETIREMENT_DRAWDOWN_OPTIONS: RetirementDrawdownOption[] = [
	{
		mode: RentDrawdownMode.ToZero,
		icon: Clock,
		copy: RetirementResources.Drawdown.toZero,
	},
	{
		mode: RentDrawdownMode.Perpetual,
		icon: InfinityIcon,
		copy: RetirementResources.Drawdown.perpetual,
	},
];

export const RETIREMENT_PHASES: InspirationTimelineStep[] = [
	{
		icon: PiggyBank,
		step: RetirementResources.Phases.setup,
		values: {
			year: RETIREMENT_PENSION_FULL_BONUS_YEAR,
			amount: formatInspirationMoney(RETIREMENT_PENSION_FULL_BONUS_FROM),
		},
	},
	{ icon: TrendingUp, step: RetirementResources.Phases.build },
	{ icon: Shield, step: RetirementResources.Phases.approaching },
	{ icon: Sunset, step: RetirementResources.Phases.living },
];
